import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getQuotes, createQuote, updateQuote, deleteQuote, getProjects, getClients, viewQuotePdf, downloadQuotePdf, adminMarkQuoteAccepted, extractErrorMessage } from "../lib/api";
import {
  PageHeader,
  DataTable,
  StatusBadge,
  LoadingSpinner,
  EmptyState,
  LockedFieldNotice,
} from "../components/shared/DataComponents";
import { useFormatters } from "../components/shared/useFormatters";
import { DestructiveConfirmDialog } from "../components/shared/DestructiveConfirmDialog";
import { ClientSelect } from "../components/shared/ClientSelect";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, FileText, Pencil, Trash2, X, Eye, Lock, Loader2, Download } from "lucide-react";

export default function QuotesPage() {
  const navigate = useNavigate();
  const { formatDate, formatCurrency } = useFormatters();
  const [quotes, setQuotes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  // EVENTSFLO-QUOTES-SEARCH-AND-ADJUSTMENT-REMOVE-UX-V1
  // Client-side search. Backend `getQuotes()` currently returns all
  // quotes for the tenant; we filter in-memory so no new API call is
  // needed. Case-insensitive substring match across quote number,
  // client/company name, project name, and status.
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({
    project_id: "",
    client_id: "",
    issue_date: new Date().toISOString().split("T")[0],
    valid_until: "",
    notes: "",
    lines: [{ description: "", quantity: "1", unit_price: "", line_total: 0 }],
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [quoteRes, projRes, clientRes] = await Promise.all([
        getQuotes(),
        getProjects(),
        getClients(),
      ]);
      setQuotes(quoteRes.data);
      setProjects(projRes.data);
      setClients(clientRes.data);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    return client?.name || "—";
  };

  const getProjectName = (projectId) => {
    if (!projectId) return "—";
    const project = projects.find((p) => p.id === projectId);
    return project?.name || "—";
  };

  const handleOpenDialog = (quote = null) => {
    if (quote) {
      // View/edit mode for status only
      setSelectedQuote(quote);
      setFormData({
        project_id: quote.project_id,
        client_id: quote.client_id,
        issue_date: quote.issue_date,
        valid_until: quote.valid_until,
        notes: quote.notes || "",
        lines: quote.lines || [],
      });
    } else {
      setSelectedQuote(null);
      setFormData({
        project_id: "",
        client_id: "",
        issue_date: new Date().toISOString().split("T")[0],
        valid_until: "",
        notes: "",
        lines: [{ description: "", quantity: "1", unit_price: "", line_total: 0 }],
      });
    }
    setDialogOpen(true);
  };

  const updateLine = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index][field] = value;
    
    // Recalculate line total
    const qty = parseFloat(newLines[index].quantity) || 0;
    const price = parseFloat(newLines[index].unit_price) || 0;
    newLines[index].line_total = qty * price;
    
    setFormData({ ...formData, lines: newLines });
  };

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { description: "", quantity: "1", unit_price: "", line_total: 0 }],
    });
  };

  const removeLine = (index) => {
    if (formData.lines.length > 1) {
      const newLines = formData.lines.filter((_, i) => i !== index);
      setFormData({ ...formData, lines: newLines });
    }
  };

  const calculateTotals = () => {
    const subtotal = formData.lines.reduce((sum, line) => sum + (line.line_total || 0), 0);
    // Tax calculated by backend from tenant settings
    return { subtotal };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (selectedQuote) {
        // Only update status, valid_until, notes
        await updateQuote(selectedQuote.id, {
          status: formData.status,
          valid_until: formData.valid_until,
          notes: formData.notes,
        });
        toast.success("Quote updated successfully");
      } else {
        const lines = formData.lines.map((line) => ({
          description: line.description,
          quantity: parseFloat(line.quantity),
          unit_price: parseFloat(line.unit_price),
          line_total: line.line_total,
        }));
        
        await createQuote({
          project_id: formData.project_id,
          client_id: formData.client_id,
          issue_date: formData.issue_date,
          valid_until: formData.valid_until,
          notes: formData.notes,
          lines,
        });
        toast.success("Quote created successfully");
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusUpdate = async (quote, newStatus) => {
    try {
      await updateQuote(quote.id, { status: newStatus });
      toast.success(`Quote ${newStatus}`);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteQuote(selectedQuote.id);
      toast.success("Quote deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedQuote(null);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  // Check if quote is locked (approved or beyond)
  const isQuoteLocked = (quote) => {
    return quote && ["approved", "rejected"].includes(quote.status);
  };

  const columns = [
    { 
      key: "quote_number", 
      label: "Quote #",
      render: (value, row) => (
        <button
          onClick={() => navigate(`/quotes/${row.id}`)}
          className="text-teal-400 hover:text-teal-300 hover:underline font-mono font-medium text-left"
          data-testid={`quote-number-link-${row.id}`}
        >
          {value}
        </button>
      ),
    },
    {
      key: "client_id",
      label: "Client",
      render: (value) => getClientName(value),
    },
    {
      key: "project_id",
      label: "Project",
      render: (value) => getProjectName(value),
    },
    {
      key: "issue_date",
      label: "Issued",
      render: (value) => formatDate(value),
    },
    {
      key: "valid_until",
      label: "Valid Until",
      render: (value) => formatDate(value),
    },
    {
      key: "total",
      label: "Total",
      align: "right",
      render: (value) => <span className="font-mono">{formatCurrency(value)}</span>,
    },
    {
      key: "status",
      label: "Status",
      render: (value) => <StatusBadge status={value} />,
    },
    {
      key: "actions",
      label: "",
      align: "right",
      render: (_, row) => {
        const locked = isQuoteLocked(row);
        return (
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                viewQuotePdf(row.id, row.quote_number, 'summary').catch(() => toast.error('Failed to open PDF'));
              }}
              title="View PDF"
              data-testid={`view-quote-pdf-${row.id}`}
            >
              <FileText className="h-4 w-4 text-teal-500" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                downloadQuotePdf(row.id, row.quote_number, 'summary').catch(() => toast.error('Failed to download PDF'));
              }}
              title="Download PDF"
              data-testid={`download-quote-pdf-${row.id}`}
            >
              <Download className="h-4 w-4 text-teal-500" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/quotes/${row.id}`);
              }}
              title="View Quote"
              data-testid={`view-quote-${row.id}`}
            >
              <Eye className="h-4 w-4 text-teal-400" />
            </Button>
            {row.status === "draft" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStatusUpdate(row, "sent");
                }}
                className="text-xs"
              >
                Send
              </Button>
            )}
            {row.status === "sent" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStatusUpdate(row, "approved");
                }}
                className="text-xs text-emerald-500"
              >
                Approve
              </Button>
            )}
            {/* EVENTSFLO-QUOTE-LIFECYCLE-CLIENT-ACCEPTANCE-ADMIN-SEND-V1 */}
            {(row.status === "sent" || row.status === "approved") && (
              <Button
                variant="ghost"
                size="sm"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!window.confirm(`Mark ${row.quote_number} as accepted?\n\nUse this only when the client has accepted outside the portal.`)) return;
                  try {
                    await adminMarkQuoteAccepted(row.id);
                    toast.success(`${row.quote_number} marked as accepted.`);
                    loadData();
                  } catch (err) {
                    toast.error(extractErrorMessage(err) || "Failed to mark accepted");
                  }
                }}
                className="text-xs text-emerald-400"
                data-testid={`mark-accepted-${row.id}`}
              >
                Mark Accepted
              </Button>
            )}
            {!locked && (
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenDialog(row);
                }}
                data-testid={`edit-quote-${row.id}`}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            {locked && (
              <Button
                variant="ghost"
                size="icon"
                disabled
                title="Quote is locked"
              >
                <Lock className="h-4 w-4 text-muted-foreground" />
              </Button>
            )}
            {row.status === "draft" ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedQuote(row);
                  setDeleteDialogOpen(true);
                }}
                data-testid={`delete-quote-${row.id}`}
              >
                <Trash2 className="h-4 w-4 text-red-400" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                disabled
                title="Only draft quotes can be deleted"
              >
                <Trash2 className="h-4 w-4 text-muted-foreground/30" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const canCreate = projects.length > 0 && clients.length > 0;
  const { subtotal, taxAmount, total } = calculateTotals();

  // EVENTSFLO-QUOTES-SEARCH-AND-ADJUSTMENT-REMOVE-UX-V1
  // Compute the filtered list once per render. When `searchQuery` is
  // blank, return the full list unchanged so pagination / sorting /
  // existing behaviour remain identical. Search matches against:
  //   • quote_number (e.g. "QT-2026-0188")
  //   • client name (via clients[] snapshot, case-insensitive)
  //   • project name (via projects[] snapshot)
  //   • status ("draft", "sent", "accepted", …)
  // Falsy field values are skipped so we never render "null" or crash.
  const filteredQuotes = (() => {
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return quotes;
    const clientById = Object.fromEntries((clients || []).map((c) => [c.id, c]));
    const projectById = Object.fromEntries((projects || []).map((p) => [p.id, p]));
    return quotes.filter((row) => {
      const num = (row.quote_number || "").toLowerCase();
      const client = clientById[row.client_id];
      const project = projectById[row.project_id];
      const clientName = (client?.name || client?.company_name || "").toLowerCase();
      const projectName = (project?.name || "").toLowerCase();
      const status = (row.status || "").toLowerCase();
      return (
        num.includes(q)
        || clientName.includes(q)
        || projectName.includes(q)
        || status.includes(q)
      );
    });
  })();

  if (loading) {
    return <LoadingSpinner size="lg" />;
  }

  return (
    <div data-testid="quotes-page">
      <PageHeader
        title="Quotes"
        subtitle="Financial quotes with snapshotted values"
        actions={
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium tracking-wide uppercase text-xs px-6"
            data-testid="add-quote-btn"
            disabled={!canCreate}
          >
            <Plus className="h-4 w-4 mr-2" />
            New Quote
          </Button>
        }
      />

      {!canCreate && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-sm p-4 mb-6 text-amber-500 text-sm">
          You need to create clients and projects before creating quotes.
        </div>
      )}

      <div className="bg-card border border-white/5 rounded-sm">
        {/* EVENTSFLO-QUOTES-SEARCH-AND-ADJUSTMENT-REMOVE-UX-V1
            Client-side search bar. Filters the loaded quotes list by
            number / client / project / status. Composes with all
            existing table behaviour (sort, actions, empty state). */}
        {quotes.length > 0 && (
          <div className="p-3 border-b border-white/5 flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search quotes by number, client, project or status…"
                className="pr-8"
                data-testid="quotes-search-input"
                aria-label="Search quotes"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="quotes-search-clear-btn"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <span
              className="text-xs text-muted-foreground"
              data-testid="quotes-search-result-count"
            >
              {searchQuery
                ? `${filteredQuotes.length} of ${quotes.length}`
                : `${quotes.length} quote${quotes.length !== 1 ? "s" : ""}`}
            </span>
          </div>
        )}
        {quotes.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No quotes yet"
            description="Create quotes with snapshotted financial values."
            action={
              canCreate && (
                <Button
                  onClick={() => handleOpenDialog()}
                  className="bg-primary text-primary-foreground"
                  data-testid="empty-add-quote-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Quote
                </Button>
              )
            }
          />
        ) : filteredQuotes.length === 0 ? (
          <div
            className="text-center py-10 text-muted-foreground text-sm"
            data-testid="quotes-search-empty"
          >
            No quotes match “{searchQuery}”. <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="underline hover:text-foreground"
            >Clear search</button>.
          </div>
        ) : (
          <DataTable columns={columns} data={filteredQuotes} />
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-secondary text-xl uppercase tracking-wide">
              {selectedQuote ? `Quote ${selectedQuote.quote_number}` : "New Quote"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!selectedQuote && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="client_id">Client *</Label>
                    <ClientSelect
                      clients={clients}
                      value={formData.client_id}
                      onChange={(value) => setFormData({ ...formData, client_id: value || '' })}
                      placeholder="Search or select client..."
                      testId="quote-client-select"
                    />
                  </div>
                  <div>
                    <Label htmlFor="project_id">Project *</Label>
                    <Select
                      value={formData.project_id}
                      onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                    >
                      <SelectTrigger data-testid="quote-project-select">
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="issue_date">Issue Date *</Label>
                    <Input
                      id="issue_date"
                      type="date"
                      value={formData.issue_date}
                      onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                      required
                      data-testid="quote-issue-date-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="valid_until">Valid Until *</Label>
                    <Input
                      id="valid_until"
                      type="date"
                      value={formData.valid_until}
                      onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                      required
                      data-testid="quote-valid-date-input"
                    />
                  </div>
                </div>

                {/* EF-QUOTE-SINGLE-SOURCE-OF-TRUTH-LOCK-V1: Tax rate removed - uses tenant settings */}

                {/* Line Items */}
                <div>
                  <Label>Line Items</Label>
                  <div className="space-y-2 mt-2">
                    {formData.lines.map((line, idx) => (
                      <div key={idx} className="flex gap-2 items-start">
                        <Input
                          value={line.description}
                          onChange={(e) => updateLine(idx, "description", e.target.value)}
                          placeholder="Description"
                          className="flex-1"
                          required
                          data-testid={`quote-line-desc-${idx}`}
                        />
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={line.quantity}
                          onChange={(e) => updateLine(idx, "quantity", e.target.value)}
                          placeholder="Qty"
                          className="w-20"
                          required
                          data-testid={`quote-line-qty-${idx}`}
                        />
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={line.unit_price}
                          onChange={(e) => updateLine(idx, "unit_price", e.target.value)}
                          placeholder="Price"
                          className="w-24"
                          required
                          data-testid={`quote-line-price-${idx}`}
                        />
                        <span className="font-mono text-sm w-24 text-right pt-2">
                          {formatCurrency(line.line_total)}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeLine(idx)}
                          disabled={formData.lines.length === 1}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addLine}
                    className="mt-2"
                    data-testid="quote-add-line-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add Line
                  </Button>
                </div>

                {/* Totals - EF-QUOTE-FORM-AND-TOGGLE-FIX-V1: TVA removed, backend handles tax */}
                <div className="border-t border-white/10 pt-4">
                  <div className="flex justify-between text-lg font-semibold">
                    <span>Subtotal</span>
                    <span className="font-mono">{formatCurrency(subtotal)}</span>
                  </div>
                </div>
              </>
            )}

            {selectedQuote && (
              <div className="space-y-4">
                <div className="bg-surface-elevated/30 p-4 rounded-sm">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Quote Details (Locked)</p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Client:</span>{" "}
                      {getClientName(selectedQuote.client_id)}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Project:</span>{" "}
                      {getProjectName(selectedQuote.project_id)}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Total:</span>{" "}
                      <span className="font-mono">{formatCurrency(selectedQuote.total)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Status:</span>{" "}
                      <StatusBadge status={selectedQuote.status} />
                    </div>
                  </div>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select
                    value={formData.status || selectedQuote.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes"
                rows={2}
                data-testid="quote-notes-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" className="bg-primary text-primary-foreground" data-testid="quote-submit-btn" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {selectedQuote ? "Update" : "Create Quote"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <DestructiveConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Quote"
        itemName={selectedQuote?.quote_number}
        itemType="quote"
        description={`Are you sure you want to delete quote "${selectedQuote?.quote_number}"? Only draft quotes can be deleted. This action is permanent and cannot be undone.`}
        onConfirm={handleDelete}
        loading={deleting}
      />
    </div>
  );
}
